import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, MapPin, Loader2, ClipboardCheck, Lock } from 'lucide-react';
import toast from 'react-hot-toast';

import caregiverService, { caregiverSignInPath } from '../../services/caregiverService';
import EmptyState from '../ui/EmptyState';
import { isLocalDevLayoutActive } from '../../utils/localDevLayout';
import flexcoaLogo from '../../assets/images/flexcoa-logo-condensed-green.png';

// The study a participant is in, so a caregiver of participants in more than
// one study can tell them apart (FECOA-2166). Empty when the server does not
// say.
const studyName = (study) => String(study?.name || study?.code || '').trim();

/**
 * Caregiver dashboard (FECOA-1320, post-login).
 *
 * Shows the caregiver's open ObsRO survey queue (caregiver-owned survey
 * windows) and the participants they are actively linked to. Tapping a survey
 * launches the reused survey-taking surface at /caregiver/survey/:assignmentId.
 */
const CaregiverDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      caregiverService.myLinks(),
      // The task queue is best-effort: a failure here must not blank the
      // dashboard, so swallow its error and fall back to an empty queue.
      caregiverService.getMyAssignments().catch(() => ({ assignments: [] })),
    ])
      .then(([linksRes, queueRes]) => {
        if (cancelled) return;
        setData(linksRes);
        setAssignments(queueRes?.assignments || []);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          navigate(caregiverSignInPath());
          return;
        }
        toast.error('Failed to load your account.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const logout = () => {
    const signInPath = caregiverSignInPath();
    caregiverService.logout();
    navigate(signInPath);
  };

  // The local dev banner sits over the top 32px of the page; leave room for it
  // only where it is shown, so deployed sites do not get an empty strip.
  const topOffset = isLocalDevLayoutActive() ? 'pt-8' : '';

  if (loading) {
    return (
      <div className={`flex min-h-screen items-center justify-center ${topOffset}`} role="status">
        <Loader2 className="h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`flex min-h-screen items-center justify-center ${topOffset}`}>
        <p className="text-sm text-gray-500 dark:text-gray-400">Unable to load your account.</p>
      </div>
    );
  }

  const links = data.links || [];

  return (
    <div className={`min-h-screen ${topOffset}`}>
      {/* Minimal caregiver-app header — no breadcrumbs, no sponsor name, no nav. */}
      <header className="border-b border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2">
            <img src={flexcoaLogo} alt="flexCOA" className="h-7 w-auto" />
          </div>
          <button
            type="button"
            onClick={logout}
            className="btn btn-secondary btn-sm"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        {/* Welcome */}
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Welcome,</p>
        <h1 className="mt-0.5 text-2xl font-semibold text-gray-900 dark:text-gray-50">
          {data.first_name} {data.last_name}
        </h1>
        <p className="mt-1 font-mono text-xs text-primary-700 dark:text-primary-400">{data.caregiver_id}</p>

        {/* ObsRO task queue — open caregiver-owned survey windows. Each card
            launches the (reused) survey-taking surface for that assignment. */}
        <h2 className="mb-3 mt-8 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Surveys to complete ({assignments.length})
        </h2>

        {assignments.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={ClipboardCheck}
              title="No tasks right now"
              description={<>You&rsquo;ll see surveys here when your study coordinator schedules one for you.</>}
              className="!py-8"
            />
          </div>
        ) : (
          <div className="space-y-3">
            {assignments.map((a) => (
              <button
                key={a.id}
                type="button"
                disabled={a.is_locked}
                onClick={() => navigate(`/caregiver/survey/${a.id}`)}
                className={
                  a.is_locked
                    ? 'w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-50 p-4 text-left opacity-60 dark:border-white/[0.07] dark:bg-gray-800/60'
                    : 'card w-full p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 dark:hover:border-primary-500/50 dark:focus-visible:ring-offset-gray-900'
                }
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">{a.survey_name}</p>
                  {a.is_locked ? (
                    <Lock className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  ) : (
                    <ClipboardCheck className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {a.is_locked
                    ? `Complete "${a.locked_by_survey_name}" first`
                    : `For participant ${a.participant?.participant_id}${
                        studyName(a.study) ? ` in ${studyName(a.study)}` : ''
                      }`}
                </p>
              </button>
            ))}
          </div>
        )}

        <h2 className="mb-3 mt-8 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Participants you support ({links.length})
        </h2>

        {links.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={User}
              title="No active participants"
              description={<>You currently have no active study tasks. Your study coordinator will let you know
              when there&rsquo;s something to complete.</>}
              className="!py-8"
            />
          </div>
        ) : (
          <div className="space-y-3">
            {links.map((link) => (
              <div
                key={link.link_id}
                className="card p-4"
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                  Participant {link.participant.participant_id}
                </p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Relationship: {link.relationship_type}
                </p>
                {studyName(link.study) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Study: {studyName(link.study)}
                  </p>
                )}
                <p className="mt-1.5 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  {link.site.site_name} ({link.site.site_code})
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaregiverDashboard;
